import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Localization from 'expo-localization';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getItem } from './storage';

// Import translations
import enTranslation from '../locales/en-US/translation.json';
import ptPTTranslation from '../locales/pt-PT/translation.json';
import ptBRTranslation from '../locales/pt-BR/translation.json';
import enManageLicenses from '../locales/en-US/manageLicenses.json';
import ptPTManageLicenses from '../locales/pt-PT/manageLicenses.json';
import ptBRManageLicenses from '../locales/pt-BR/manageLicenses.json';
import enManageDrivers from '../locales/en-US/manageDrivers.json';
import ptPTManageDrivers from '../locales/pt-PT/manageDrivers.json';
import ptBRManageDrivers from '../locales/pt-BR/manageDrivers.json';
import enManageVehicles from '../locales/en-US/manageVehicles.json';
import ptPTManageVehicles from '../locales/pt-PT/manageVehicles.json';
import ptBRManageVehicles from '../locales/pt-BR/manageVehicles.json';
import enLogin from '../locales/en-US/login.json';
import ptPTLogin from '../locales/pt-PT/login.json';
import ptBRLogin from '../locales/pt-BR/login.json';
import registerEn from '../locales/en-US/register.json';
import registerPtPT from '../locales/pt-PT/register.json';
import registerPtBR from '../locales/pt-BR/register.json';
import onboardingEn from '../locales/en-US/onboarding.json';
import onboardingPtPT from '../locales/pt-PT/onboarding.json';
import onboardingPtBR from '../locales/pt-BR/onboarding.json';
import gpsEn from '../locales/en-US/gps.json';
import gpsPtPT from '../locales/pt-PT/gps.json';
import gpsPtBR from '../locales/pt-BR/gps.json';
import enDashboard from '../locales/en-US/dashboard.json';
import ptPTDashboard from '../locales/pt-PT/dashboard.json';
import ptBRDashboard from '../locales/pt-BR/dashboard.json';
import enPaywall from '../locales/en-US/paywall.json';
import ptPTPaywall from '../locales/pt-PT/paywall.json';
import ptBRPaywall from '../locales/pt-BR/paywall.json';
import enWorkCompany from '../locales/en-US/workCompany.json';
import ptPTWorkCompany from '../locales/pt-PT/workCompany.json';
import ptBRWorkCompany from '../locales/pt-BR/workCompany.json';
import enPartner from '../locales/en-US/partner.json';
import ptPartner from '../locales/pt-PT/partner.json';
import brPartner from '../locales/pt-BR/partner.json';

// Constants
export const LANGUAGE_KEY = '@app_language';
const SUPPORTED_LANGUAGES = ['en', 'pt', 'pt_BR'];

// Resources for i18next
const resources = {
  en:     {
    translation: { ...enTranslation, ...gpsEn },
    dashboard: enDashboard,
    login: enLogin,
    register: registerEn,
    onboarding: onboardingEn,
    manageLicenses: enManageLicenses,
    manageDrivers: enManageDrivers,
    manageVehicles: enManageVehicles,
    paywall: enPaywall,
    workCompany: enWorkCompany,
    partner: enPartner
  },
  pt:     {
    translation: { ...ptPTTranslation, ...gpsPtPT },
    dashboard: ptPTDashboard,
    login: ptPTLogin,
    register: registerPtPT,
    onboarding: onboardingPtPT,
    manageLicenses: ptPTManageLicenses,
    manageDrivers: ptPTManageDrivers,
    manageVehicles: ptPTManageVehicles,
    paywall: ptPTPaywall,
    workCompany: ptPTWorkCompany,
    partner: ptPartner
  },
  pt_BR:  {
    translation: { ...ptBRTranslation, ...gpsPtBR },
    dashboard: ptBRDashboard,
    login: ptBRLogin,
    register: registerPtBR,
    onboarding: onboardingPtBR,
    manageLicenses: ptBRManageLicenses,
    manageDrivers: ptBRManageDrivers,
    manageVehicles: ptBRManageVehicles,
    paywall: ptBRPaywall,
    workCompany: ptBRWorkCompany,
    partner: brPartner
  }
};

// Function to get system language via expo-localization
const getSystemLanguage = (): string => {
  const locale = Localization.getLocales()[0]?.languageTag.toLowerCase() ?? 'pt';
  if (locale === 'pt-br' || locale === 'pt_br') return 'pt_BR';
  const primary = locale.split(/[-_]/)[0];
  if (primary === 'en') return 'en';
  if (primary === 'pt') return 'pt';
  return 'pt';
};

// Local storage helpers
export const getStoredLanguage = async (): Promise<string> => {
  try {
    const lang = await AsyncStorage.getItem(LANGUAGE_KEY);
    if (lang && SUPPORTED_LANGUAGES.includes(lang)) {
      return lang;
    }
    return getSystemLanguage();
  } catch (error) {
    console.error('Error reading language preference:', error);
    return getSystemLanguage();
  }
};

export const setStoredLanguage = async (language: string): Promise<boolean> => {
  try {
    const norm = language;
    if (!SUPPORTED_LANGUAGES.includes(norm)) {
      throw new Error(`Unsupported language: ${language}`);
    }
    await AsyncStorage.setItem(LANGUAGE_KEY, norm);
    return true;
  } catch (error) {
    console.error('Error saving language preference:', error);
    return false;
  }
};

// Initialize i18next
i18n.use(initReactI18next).init({
  resources,
  lng: 'pt',
  fallbackLng: 'pt',
  supportedLngs: SUPPORTED_LANGUAGES,
  ns: ['translation', 'dashboard', 'login', 'register', 'onboarding', 'manageLicenses', 'manageDrivers', 'manageVehicles', 'paywall'],
  defaultNS: 'translation',
  fallbackNS: ['login', 'translation', 'dashboard', 'register', 'onboarding', 'manageLicenses', 'manageDrivers', 'manageVehicles', 'paywall'],
  interpolation: { 
    escapeValue: false,
    formatSeparator: ',',
    format: (value, format, lng) => {
      if (format === 'count') {
        return value;
      }
      return value;
    }
  },
  react: { useSuspense: false }
});

// Initialization function handling logged-in vs guest
export const initializeI18n = async (): Promise<typeof i18n> => {
  try {
    // First, get language from storage or system (fast, doesn't need API)
    // This allows the app to start rendering immediately with a valid language
    const storedLang = await getStoredLanguage();
    let languageToUse = storedLang || getSystemLanguage();
    
    // Set the language immediately so app can render without waiting for API
    await i18n.changeLanguage(languageToUse);
    
    // Then, if user is logged in, try to get language from backend in background
    // This is non-blocking - if it's slow or fails, we already have a valid language
    const token = await getItem('userToken');
    if (token) {
      // Fire and forget - fetch language from backend, update if different
      import('../api/apiService').then(({ getUserLanguage }) => {
        // Use a short timeout so we don't hang
        const apiPromise = getUserLanguage();
        const timeoutPromise = new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('API timeout')), 1500)
        );
        
        Promise.race([apiPromise, timeoutPromise])
          .then(response => {
            if (response.ok && response.data.language) {
              const backendLanguage = response.data.language;
              if (backendLanguage !== languageToUse) {
                setStoredLanguage(backendLanguage);
                i18n.changeLanguage(backendLanguage);
              }
            }
          })
          .catch(() => {
            // API call failed or timed out - continue with stored/system language
            // This is expected if backend is slow, so we don't log it
          });
      }).catch(() => {
        // Import failed - continue with stored language
      });
    }
    
    return i18n;
  } catch (error) {
    // Use default language on any error
    await i18n.changeLanguage('pt');
    return i18n;
  }
};

export default i18n; 